import React, { useEffect, useMemo, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranscription } from "@features/transcription/hooks/useTranscription";
import { BreadcrumbSchema, Seo } from "@shared/components";
import { BLOG_POSTS } from "@shared/config/blog-posts";
import { type SeoCluster } from "@shared/config/seo-pages";
import { HOME_FAQ, ORGANIZATION_SCHEMA, softwareApplicationSchema, WEBSITE_SCHEMA } from "@shared/config/seo-schemas";
import { useAuth } from "@shared/contexts/auth-context";
import { useToast } from "@shared/helper_lib/hooks/use-toast";
import { CtaSection } from "@widgets/CtaSection";
import { FaqSection } from "@widgets/FaqSection/FaqSection";
import { FeaturesSection } from "@widgets/FeaturesSection";
import { HeroSection } from "@widgets/HeroSection";
import { RelatedPages } from "@widgets/RelatedPages";
import { UploadSection, type UploadSectionRef } from "@widgets/UploadSection";

interface MainPageProps {
  customTitle?: React.ReactNode;
  customSubtitle?: string;
  metaTitle?: string;
  metaDescription?: string;
  faq?: { question: string; answer: string }[];
  seoDescription?: React.ReactNode;
  cluster?: SeoCluster;
  /** Каноникал на другую страницу — для URL-дублей (см. SeoPageConfig.canonicalPath) */
  canonicalPath?: string;
  /** Slug'и статей блога по теме страницы (см. SeoPageConfig.relatedArticles) */
  relatedArticles?: string[];
}

const DEFAULT_META_TITLE = "Полотно — расшифровка аудио и видео в текст онлайн";
const DEFAULT_META_DESCRIPTION =
  "Полотно (Polotno) — расшифровка записи нейросетью с разделением по спикерам и таймкодами. Файлы до 2 ГБ, ссылки на YouTube, VK Video и Rutube, бот в Telegram. Пробный период — 150 минут за 99 ₽.";

/** Статичная линейка фактов (вместо pill-бейджей) */
const StatsStrip: React.FC = () => {
  const stats = [
    { value: "95–99%", label: "точность на записях хорошего качества" },
    { value: "10–15%", label: "от длительности — время обработки" },
    { value: "4 формата", label: "TXT · DOCX · PDF · SRT" },
    { value: "99", label: "языков, включая языки СНГ" }
  ];

  return (
    <section className="py-10 border-b border-border bg-background">
      <div className="container">
        <div className="grid grid-cols-2 gap-8 md:grid-cols-4">
          {stats.map(stat => (
            <div key={stat.value} className="border-t-2 border-foreground pt-4">
              <div className="text-3xl font-bold tracking-tight">{stat.value}</div>
              <div className="mt-1 text-sm text-muted-foreground">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/** Блок «Как это работает» — нумерованная временная лента */
const HowItWorks: React.FC = () => {
  const steps = [
    {
      tag: "Шаг 01",
      title: "Загрузите файл или ссылку",
      text: "Аудио, видео или открытая публикация с YouTube, VK, Rutube, OK или Дзена. До 2 ГБ."
    },
    {
      tag: "Шаг 02",
      title: "Выберите язык и режим",
      text: "Русский, английский и языки СНГ. Быстрый режим — для чистых записей, «Качество» — для сложного звука."
    },
    {
      tag: "Шаг 03",
      title: "Заберите результат",
      text: "Текст с таймкодами и спикерами, AI-сводка и выгрузка в нужном формате."
    }
  ];

  return (
    <section className="py-16 md:py-24 bg-muted/30">
      <div className="container">
        <div className="mb-10">
          <p className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Три шага</p>
          <h2 className="text-3xl font-bold tracking-tight md:text-4xl">От файла до готового текста</h2>
          <p className="mt-3 max-w-2xl text-lg text-muted-foreground">
            Без настроек и конвертаций — сервис сам достаёт звук и разбирается с форматом.
          </p>
        </div>

        <div className="space-y-6">
          {steps.map(step => (
            <div
              key={step.tag}
              className="grid grid-cols-[auto,1fr] md:grid-cols-[16rem,1fr] gap-4 md:gap-8 items-start"
            >
              <div className="hidden md:block">
                <div className="text-sm font-bold uppercase tracking-wider text-muted-foreground">{step.tag}</div>
              </div>
              <div className="border-b border-border pb-6">
                <h3 className="text-xl font-semibold">{step.title}</h3>
                <p className="mt-2 max-w-2xl text-muted-foreground">{step.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const MainPage: React.FC<MainPageProps> = ({
  customTitle,
  customSubtitle,
  metaTitle,
  metaDescription,
  faq,
  seoDescription,
  cluster,
  canonicalPath,
  relatedArticles: relatedArticleSlugs
}) => {
  const { startTranscription, reset } = useTranscription();
  const { openAuthModal } = useAuth();
  const { toast } = useToast();
  const uploadSectionRef = useRef<UploadSectionRef>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const hasTriggeredUpload = useRef(false);
  const hasHandledAuthError = useRef(false);
  const hasHandledTryChatEmpty = useRef(false);

  useEffect(() => {
    if (location.state?.tryChatEmpty && !hasHandledTryChatEmpty.current) {
      hasHandledTryChatEmpty.current = true;
      toast({
        title: "Сначала загрузите аудио или видео",
        description: "Чат «Спросить по тексту» открывается из истории — он доступен после первой транскрипции."
      });
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate, toast]);

  useEffect(() => {
    if (location.state?.openUpload && !hasTriggeredUpload.current) {
      hasTriggeredUpload.current = true;
      uploadSectionRef.current?.openFileDialog();
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  useEffect(() => {
    const authError = location.state?.authError;

    if (authError && !hasHandledAuthError.current) {
      hasHandledAuthError.current = true;

      toast({
        title: authError.isNewUser ? "Аккаунт не найден" : "Ошибка авторизации",
        description: authError.message,
        variant: "destructive"
      });

      if (authError.isNewUser) {
        openAuthModal("signup");
      }

      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate, openAuthModal, toast]);

  const handleUploadComplete = async (
    fileIdOrUrl: number | string,
    fileName: string,
    language: string,
    model: string,
    estimatedMinutes?: number,
    detectLanguage?: boolean,
    preview?: boolean
  ): Promise<void> => {
    const deepgramModel = model === "quality" ? "assemblyai" : "nova-3";
    await startTranscription(fileIdOrUrl, fileName, language, deepgramModel, estimatedMinutes, detectLanguage, preview);
  };

  const handleStart = (): void => {
    uploadSectionRef.current?.openFileDialog();
    reset();
  };

  const isHome = location.pathname === "/";
  const pageFaq = faq && faq.length > 0 ? faq : isHome ? HOME_FAQ : undefined;
  const title = metaTitle || DEFAULT_META_TITLE;
  const description = metaDescription || DEFAULT_META_DESCRIPTION;

  /**
   * Статьи блога по теме страницы. Связь идёт по slug, поэтому удалённая или
   * переименованная статья просто не отрисуется, а не уронит страницу.
   */
  const relatedArticles = useMemo(
    () =>
      (relatedArticleSlugs ?? [])
        .map(slug => BLOG_POSTS.find(post => post.slug === slug))
        .filter((post): post is (typeof BLOG_POSTS)[number] => Boolean(post)),
    [relatedArticleSlugs]
  );

  // На главной отдаём полный набор схем сайта, на посадочных — только описание сервиса
  const jsonLd: Record<string, unknown>[] = isHome
    ? [ORGANIZATION_SCHEMA, WEBSITE_SCHEMA, softwareApplicationSchema()]
    : [softwareApplicationSchema()];

  if (pageFaq) {
    jsonLd.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: pageFaq.map(item => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: { "@type": "Answer", text: item.answer }
      }))
    });
  }

  return (
    <div className="overflow-hidden">
      <Seo title={title} description={description} path={canonicalPath || location.pathname} jsonLd={jsonLd} />
      {!isHome && <BreadcrumbSchema items={[{ name: "Главная", url: "/" }, { name: title.split("|")[0].trim() }]} />}

      {/* 1. Hero: короткий фактический заголовок + CTA */}
      <HeroSection title={customTitle} subtitle={customSubtitle} compact={!isHome} />

      {/* 2. Загрузка — главное действие в первом экране */}
      <UploadSection ref={uploadSectionRef} onUploadComplete={handleUploadComplete} />

      {/* 3. Линейка фактов */}
      <StatsStrip />

      {/* 4. Как это работает */}
      <HowItWorks />

      {seoDescription && (
        <section className="py-12 bg-muted/30">
          <div className="container max-w-4xl">
            <div className="prose prose-sm md:prose-base dark:prose-invert text-muted-foreground space-y-4">
              {seoDescription}
            </div>
          </div>
        </section>
      )}

      {/* 5. Возможности */}
      <FeaturesSection cluster={cluster} />

      {/* 8. FAQ */}
      <FaqSection items={pageFaq} />

      {/* 9. Перелинковка по тематической группе */}
      <RelatedPages currentPath={location.pathname} cluster={cluster} />

      {/* 10. Статьи блога по теме страницы — ссылки в направлении «посадочная → статья» */}
      {relatedArticles.length > 0 && (
        <section className="border-b border-border py-12">
          <div className="container max-w-4xl">
            <p className="mb-5 text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
              Разбор в блоге
            </p>
            <div className="flex flex-col gap-5">
              {relatedArticles.map(post => (
                <Link key={post.slug} to={`/blog/${post.slug}`} className="group flex flex-col gap-1.5">
                  <span className="text-[17px] font-semibold leading-snug tracking-[-0.01em] text-foreground transition-colors group-hover:text-primary md:text-lg">
                    {post.title}
                  </span>
                  <span className="text-sm leading-relaxed text-muted-foreground">{post.excerpt}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 11. CTA */}
      <CtaSection onStart={handleStart} />
    </div>
  );
};
